"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { CartItem, CartMetadata, CartState, CartContextType } from "./types";
import { capture } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

const STORAGE_KEY = "plantista-cart";

type CartAction =
  | { type: "ADD_ITEMS"; items: CartItem[] }
  | { type: "UPDATE_QUANTITY"; genusAliasId: string; quantity: number }
  | { type: "REMOVE_ITEM"; genusAliasId: string }
  | { type: "SET_METADATA"; metadata: CartMetadata }
  | { type: "CLEAR" }
  | { type: "HYDRATE"; state: CartState };

const initialState: CartState = {
  items: [],
  metadata: {},
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEMS": {
      // Replace all items (fresh add from shopping list)
      return { ...state, items: action.items };
    }
    case "UPDATE_QUANTITY": {
      return {
        ...state,
        items: state.items.map((item) =>
          item.genusAliasId === action.genusAliasId
            ? { ...item, quantity: Math.max(1, action.quantity) }
            : item
        ),
      };
    }
    case "REMOVE_ITEM": {
      return {
        ...state,
        items: state.items.filter(
          (item) => item.genusAliasId !== action.genusAliasId
        ),
      };
    }
    case "SET_METADATA": {
      return { ...state, metadata: { ...state.metadata, ...action.metadata } };
    }
    case "CLEAR": {
      return initialState;
    }
    case "HYDRATE": {
      return action.state;
    }
    default:
      return state;
  }
}

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Hydrate from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        dispatch({ type: "HYDRATE", state: parsed });
      }
    } catch (error) {
      console.error("[Cart] Failed to restore cart from session:", error);
    }
  }, []);

  // Persist to sessionStorage on change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("[Cart] Failed to save cart to session:", error);
    }
  }, [state]);

  const subtotal = state.items.reduce((sum, item) => {
    if (item.unitPrice !== null) {
      return sum + item.unitPrice * item.quantity;
    }
    return sum;
  }, 0);

  const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);

  const hasUnpricedItems = state.items.some((item) => item.unitPrice === null);

  const value: CartContextType = {
    items: state.items,
    metadata: state.metadata,
    subtotal,
    totalItems,
    hasUnpricedItems,
    addItems: (items) => {
      dispatch({ type: "ADD_ITEMS", items });
      // cart_add — single chokepoint so it fires on every surface (PLA2-366).
      capture(ANALYTICS_EVENTS.CART_ADD, {
        item_count: items.reduce((n, it) => n + (it.quantity || 0), 0),
      });
    },
    updateQuantity: (genusAliasId, quantity) =>
      dispatch({ type: "UPDATE_QUANTITY", genusAliasId, quantity }),
    removeItem: (genusAliasId) => {
      dispatch({ type: "REMOVE_ITEM", genusAliasId });
      capture(ANALYTICS_EVENTS.CART_REMOVE);
    },
    setMetadata: (metadata) => dispatch({ type: "SET_METADATA", metadata }),
    clearCart: () => dispatch({ type: "CLEAR" }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
